import { useCallback, useState } from "react";

import { CompetencySuggestion, EditableCompetencyFields } from "../types";

import { getInterviewerNotesKey } from "../store/helpers";

import internalRequest, { ErrorResponse } from "../../../api/services/defs/internal-api/internal-base-request";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type ResolvedCompetency = {
	name: string;
	description: string;
	levels: Array<{ rank: number; name: string; description: string }>;
};

export const useCompetencyCreation = () => {
	const [isSaving, setIsSaving] = useState(false);
	const activeInterviewerHarness = useAskPerformYardStore((store) => store.activeInterviewerHarness);
	const setCompetencyFrameworkStatus = useAskPerformYardStore((store) => store.setCompetencyFrameworkStatus);
	const addStaticAssistantMessage = useAskPerformYardStore((store) => store.addStaticAssistantMessage);

	const saveFramework = useCallback(
		async (resolvedCompetencies: ResolvedCompetency[]) => {
			if (useAskPerformYardStore.getState().competencyFramework?.status === "saving") return;
			setIsSaving(true);
			setCompetencyFrameworkStatus("saving");
			try {
				await internalRequest("/v1/competencies/bulk", {
					method: "POST",
					body: JSON.stringify({ competencies: resolvedCompetencies }),
				});
				// Clear interview notes from sessionStorage on successful save
				if (activeInterviewerHarness) {
					sessionStorage.removeItem(getInterviewerNotesKey(activeInterviewerHarness));
				}
				useAskPerformYardStore.getState().deactivateInterviewerHarness();
				setCompetencyFrameworkStatus("saved");
			} catch (error) {
				if (error instanceof ErrorResponse && error.response.status === 409) {
					const errorMessage =
						(error.data as { message?: string })?.message ||
						"Failed to save competencies. Please try again.";
					addStaticAssistantMessage(
						`${errorMessage}. To fix this, either click **Edit** on the framework card to rename them yourself, or reply here with your preferred names and I'll update the framework.`
					);
					setCompetencyFrameworkStatus("pending");
					return;
				}
				const errorMessage =
					error instanceof ErrorResponse
						? ((error.data as { message?: string })?.message ?? error.message)
						: error instanceof Error
							? error.message
							: "Failed to save competencies. Please try again.";
				setCompetencyFrameworkStatus("error", errorMessage || "Failed to save competencies. Please try again.");
			} finally {
				setIsSaving(false);
			}
		},
		[activeInterviewerHarness, addStaticAssistantMessage, setCompetencyFrameworkStatus]
	);

	return { saveFramework, isSaving };
};

export const resolveCompetencies = (
	suggestions: CompetencySuggestion[],
	editedFields: Record<string, Partial<EditableCompetencyFields>>
) =>
	suggestions.map((suggestion) => ({
		name: editedFields[suggestion.id]?.name ?? suggestion.name,
		description: editedFields[suggestion.id]?.description ?? suggestion.description,
		levels: editedFields[suggestion.id]?.levels ?? suggestion.levels,
	}));
